/**
 * Botón del kiosco (design-system §7 · Botón y §8.2 · Activación).
 *
 * - `primary` (por defecto): Coral 500 sólido, 56 px de alto (`--fm-control-lg`, ≥ 44 px táctiles de
 *   §10), etiqueta Nunito 700 18 px; es el "ACTIVAR" del formulario de código. Hover Coral 600,
 *   pressed Coral 700. Solo cumple AA con etiqueta grande y bold (§3.3): por eso no hay tamaño menor
 *   para el primario.
 * - `secondary`: contorno marino ("Configurar de nuevo"). `ghost`: terciario.
 * - En una tablet el botón **no** ocupa todo el ancho por defecto (`fullWidth=false`): la pantalla
 *   de activación lo limita a la anchura del formulario.
 * - Foco: anillo 2 px `--fm-focus` con offset 2 px. `loading` reemplaza la etiqueta por un texto de
 *   progreso (nunca un spinner solo) y bloquea el botón. Solo tokens `--fm-*`.
 * - `shrink-0`: en una pantalla baja (teléfono en horizontal) el botón es lo último que puede
 *   ceder alto; sin él, el flex de la pantalla lo comprimía por debajo de los 44 px táctiles (§10).
 */
import { t } from '../../i18n';

import { cn } from './cn';

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'lg' | 'md';

const BASE_CLASSES =
  'inline-flex shrink-0 items-center justify-center gap-fm-2 rounded-fm-md select-none whitespace-nowrap ' +
  'transition-colors duration-fm-fast ease-fm-standard ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus ' +
  'disabled:opacity-45 disabled:shadow-none disabled:pointer-events-none ' +
  'motion-reduce:transition-none';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-fm-primary text-fm-on-primary font-display font-bold tracking-wide ' +
    'hover:bg-fm-primary-hover active:bg-fm-primary-compact',
  secondary:
    'bg-transparent text-fm-text border-2 border-fm-text font-ui font-semibold ' +
    'hover:bg-fm-bg-sunken active:bg-fm-bg-sunken',
  ghost:
    'bg-transparent text-fm-text font-ui font-medium hover:bg-fm-bg-sunken active:bg-fm-bg-sunken',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  lg: 'h-fm-control-lg px-fm-10 text-fm-lg',
  md: 'h-fm-control-md px-fm-6 text-fm-base',
};

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  /** Por defecto `lg` para `primary` y `md` para el resto. */
  size?: ButtonSize;
  /** Ancho completo del contenedor (por defecto no: la pantalla es una tablet). */
  fullWidth?: boolean;
  className?: string;
}

/** Clases del botón para reutilizarlas en un `<Link>` u otro elemento. */
export function buttonClasses({
  variant = 'primary',
  size,
  fullWidth = false,
  className,
}: ButtonClassOptions = {}): string {
  const resolvedSize: ButtonSize = size ?? (variant === 'primary' ? 'lg' : 'md');
  return cn(
    BASE_CLASSES,
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[resolvedSize],
    fullWidth && 'w-full',
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonClassOptions {
  /** Muestra el texto de progreso y deshabilita el botón. */
  loading?: boolean;
  /** Texto de progreso; por defecto `common.states.processing`. */
  loadingText?: string;
  /** Icono Lucide a la izquierda (se oculta mientras carga). */
  icon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'primary',
  size,
  fullWidth = false,
  loading = false,
  loadingText,
  icon,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      className={buttonClasses({ variant, size, fullWidth, className })}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span>{loadingText ?? t('common.states.processing')}</span>
      ) : (
        <>
          {icon ? <span aria-hidden="true">{icon}</span> : null}
          {children}
        </>
      )}
    </button>
  );
}
